import { Component, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";

interface Props {
  id: string;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage?: string;
}

export class DeliberationErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMessage: error?.message };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    // eslint-disable-next-line no-console
    console.error("[deliberation] render crash caught by ErrorBoundary", error, info?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return <DeliberationCrashFallback id={this.props.id} />;
    }
    return this.props.children;
  }
}

function DeliberationCrashFallback({ id }: { id: string }) {
  const navigate = useNavigate();

  return (
    <div
      data-testid="deliberation-crash-fallback"
      style={{
        minHeight: "calc(100vh - 24px)",
        display: "grid",
        placeItems: "center",
        padding: 32,
        background: "var(--ivory)",
        textAlign: "center",
      }}
    >
      <div style={{ maxWidth: 540 }}>
        <div
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: 10,
            letterSpacing: 2.5,
            textTransform: "uppercase",
            color: "var(--gold-deep)",
            marginBottom: 14,
          }}
        >
          Le Conseil siège encore
        </div>
        <p
          style={{
            fontFamily: '"Playfair Display", serif',
            fontStyle: "italic",
            fontSize: 20,
            lineHeight: 1.45,
            color: "var(--charcoal)",
            margin: "0 0 28px",
          }}
        >
          Une erreur d'affichage s'est produite pendant la délibération. Ne
          vous inquiétez pas — le Conseil continue à siéger en arrière-plan.
          Vous retrouverez votre saisine dans les Archives.
        </p>
        <div style={{ display: "inline-flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
          <button
            type="button"
            onClick={() => navigate({ to: "/archives" })}
            style={{
              background: "var(--navy)",
              border: "1px solid var(--navy)",
              color: "var(--ivory)",
              padding: "10px 18px",
              fontFamily: '"DM Mono", monospace',
              fontSize: 11,
              letterSpacing: 2,
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            Voir mes saisines en cours →
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              background: "transparent",
              border: "1px solid var(--charcoal)",
              color: "var(--charcoal)",
              padding: "10px 18px",
              fontFamily: '"DM Mono", monospace',
              fontSize: 11,
              letterSpacing: 2,
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            Réessayer
          </button>
        </div>
      </div>
    </div>
  );
}
